import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"

import debounce from "lodash/debounce"

import type { SharedChatProps } from "../components/chat-footer"
import type { Accept } from "react-dropzone"

import { useViewportBelow } from "@/hooks/use-viewport-below"
import TokenService from "@/services/token-service"

import { useFileContent } from "./use-file-content"

type UseChatFooterProps = SharedChatProps & {
  acceptedFileTypes?: Accept
  chatboxRef: React.RefObject<HTMLElement>
  allowFileOnlySend?: boolean
  hasExternalContent?: boolean
}

const FRONTEND_SAFETY_MARGIN = 6000
const CHAT_TEXTAREA_MIN_HEIGHT = 16
const CHAT_TEXTAREA_MAX_HEIGHT_RATIO = 0.4

export function useChatFooter({
  chatboxRef,
  disableSend,
  clearOnSend,
  initQuestion,
  onSend,
  onQueue,
  disableFileSelection,
  onFilesAdded,
  acceptedFileTypes,
  availableFiles,
  changeTokenCounts,
  totalTokenCount,
  maxTokens,
  allowFileOnlySend = false,
  hasExternalContent = false,
}: UseChatFooterProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const reportedTextTokenCountRef = useRef(0)
  const isPhoneViewport = useViewportBelow({ breakpoint: 768 })

  const tokenService = TokenService.getInstance()

  const [question, setQuestion] = useState<string>("")
  const [isTextareaFocused, setIsTextareaFocused] = useState(false)

  useFileContent({
    availableFiles,
    onTokenCountChange: (tokenCount) => changeTokenCounts({ files: tokenCount }),
  })

  const textTokenCount = tokenService.countTokens(question || "")
  const hasTokensExceeded = totalTokenCount > maxTokens
  const dynamicTextLimit = maxTokens - FRONTEND_SAFETY_MARGIN
  const isTextOverHardLimit = textTokenCount > dynamicTextLimit
  const shouldBlockSending = hasTokensExceeded && isTextOverHardLimit

  const hasQuestionText = question.trim().length > 0
  const hasFilesForFileOnlySend = allowFileOnlySend && (availableFiles?.length ?? 0) > 0
  const hasValidContent = hasQuestionText || hasFilesForFileOnlySend || hasExternalContent

  const sendQuestionDisabled = disableSend || shouldBlockSending || !hasValidContent
  // File-only / ask-about-only sends aren't well-defined once replayed later
  // from a text queue, so only a typed question is queueable — everything
  // else falls back to the normal blocked no-op.
  const canQueue = Boolean(onQueue) && disableSend && !shouldBlockSending && hasQuestionText
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    textarea.style.height = "0px"
    const maxHeight = Math.max(
      CHAT_TEXTAREA_MIN_HEIGHT,
      Math.floor(window.innerHeight * CHAT_TEXTAREA_MAX_HEIGHT_RATIO)
    )
    const measuredHeight = textarea.scrollHeight
    const safeMeasuredHeight =
      Number.isFinite(measuredHeight) && measuredHeight > 0
        ? measuredHeight
        : CHAT_TEXTAREA_MIN_HEIGHT
    const nextHeight = Math.min(Math.max(safeMeasuredHeight, CHAT_TEXTAREA_MIN_HEIGHT), maxHeight)
    textarea.style.height = `${nextHeight}px`
  }, [])

  useEffect(() => {
    const debounceUpdateTokenCounter = debounce(UpdateTokenCounter, 500)
    debounceUpdateTokenCounter()

    return () => {
      debounceUpdateTokenCounter.cancel()
    }
  }, [question])

  useEffect(() => {
    if (initQuestion) {
      setQuestion(initQuestion)
    }
  }, [initQuestion])

  useLayoutEffect(() => {
    adjustTextareaHeight()
  }, [adjustTextareaHeight, question])

  useEffect(() => {
    const handleResize = () => {
      adjustTextareaHeight()
    }
    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [adjustTextareaHeight])

  useEffect(() => {
    const textarea = textareaRef.current
    const resizeTarget = textarea?.parentElement
    if (!resizeTarget || typeof ResizeObserver === "undefined") return

    let lastWidth = resizeTarget.getBoundingClientRect().width
    const observer = new ResizeObserver((entries) => {
      const nextWidth = entries[0]?.contentRect.width ?? resizeTarget.getBoundingClientRect().width
      if (Math.abs(nextWidth - lastWidth) < 1) return
      lastWidth = nextWidth
      adjustTextareaHeight()
    })

    observer.observe(resizeTarget)
    return () => {
      observer.disconnect()
    }
  }, [adjustTextareaHeight])

  useEffect(() => {
    const handleFocus = () => setIsTextareaFocused(true)
    const handleBlur = (e: FocusEvent) => {
      if (e.relatedTarget !== chatboxRef.current) {
        setIsTextareaFocused(false)
      }
    }

    const textarea = textareaRef.current
    if (textarea) {
      textarea.addEventListener("focus", handleFocus)
      textarea.addEventListener("blur", handleBlur)
    }

    return () => {
      if (textarea) {
        textarea.removeEventListener("focus", handleFocus)
        textarea.removeEventListener("blur", handleBlur)
      }
    }
  }, [textareaRef])

  const handleTextAreaClick = () => {
    if (textareaRef.current) {
      textareaRef.current.focus()
    }
  }

  function UpdateTokenCounter() {
    const tokenCount = tokenService.countTokens(question || "")
    reportedTextTokenCountRef.current = tokenCount
    changeTokenCounts({ text: tokenCount })
  }

  const getReportedTextTokenCount = useCallback(() => reportedTextTokenCountRef.current, [])

  const clearQuestionInput = () => {
    setQuestion("")
    if (textareaRef.current) {
      textareaRef.current.value = ""
    }
    adjustTextareaHeight()
  }

  const sendQuestion = () => {
    if (sendQuestionDisabled) {
      if (canQueue) {
        const queueResult = onQueue?.(question || "")
        // Host may reject (e.g. `/deep` excluded from the v1 queue).
        if (queueResult !== false) {
          clearQuestionInput()
        }
      }
      return
    }

    const sendResult = onSend(question || "")
    if (sendResult === false) {
      return
    }

    if (clearOnSend) {
      clearQuestionInput()
    }
  }

  const handleKeyDown = (ev: React.KeyboardEvent<Element>) => {
    if (ev.key !== "Enter" || ev.shiftKey) return
    // Phones normally don't send on Enter (newline UX). Allow Enter when the
    // only available action is queueing — otherwise mobile has no queue path
    // while Stop is showing during a busy turn.
    if (isPhoneViewport && !canQueue) return
    ev.preventDefault()
    sendQuestion()
  }

  const handleFilePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (disableFileSelection) {
      return
    }

    const acceptedFileMimeTypes = Object.keys(acceptedFileTypes ?? {})

    const items = e.clipboardData.items
    const filesToAdd: File[] = []

    for (let i = 0; i < items.length; i++) {
      const itemType = items[i].type
      if (
        acceptedFileMimeTypes.includes(itemType) ||
        (acceptedFileMimeTypes.includes("image/*") && itemType.includes("image"))
      ) {
        const newFile = items[i].getAsFile()
        if (newFile) {
          filesToAdd.push(newFile)
        }
      }
    }

    if (filesToAdd.length === 0) {
      return
    }

    const didAddFiles = onFilesAdded?.(filesToAdd)
    if (didAddFiles !== false) {
      e.preventDefault()
    }
  }

  return {
    handleTextAreaClick,
    handleKeyDown,
    textareaRef,
    handleFilePaste,
    setQuestion,
    question,
    adjustTextareaHeight,
    sendQuestion,
    sendQuestionDisabled,
    canQueue,
    isTextareaFocused,
    textTokenCount,
    hasTokensExceeded,
    isTextOverHardLimit,
    shouldBlockSending,
    getReportedTextTokenCount,
  }
}
